import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AlertTriangle, CheckCircle, Trash2, Shield, Clock, Database } from 'lucide-react';
import api from '../lib/api';

export default function DeleteAccountRequestPage() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/request-account-deletion', { email, reason });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nell\'invio della richiesta. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <CardTitle>Richiesta Inviata</CardTitle>
            <CardDescription>
              La tua richiesta di eliminazione account OBC Cloud è stata ricevuta.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center text-muted-foreground">
            <p className="mb-4">
              Riceverai un'email di conferma all'indirizzo <strong>{email}</strong> con le istruzioni per completare l'eliminazione.
            </p>
            <p className="text-sm">
              L'elaborazione della richiesta può richiedere fino a 30 giorni lavorativi, come previsto dalla normativa GDPR.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">OBC Cloud</h1>
          <p className="text-muted-foreground">Richiesta di Eliminazione Account e Dati</p>
        </div>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <CardTitle>Elimina il tuo Account</CardTitle>
                <CardDescription>
                  Richiedi l'eliminazione permanente del tuo account OBC Cloud
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Steps Section */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Come richiedere l'eliminazione del tuo account
              </h3>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Inserisci l'indirizzo email associato al tuo account OBC Cloud nel modulo sottostante</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Riceverai un'email di conferma con un link per verificare la tua identità</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Il nostro team elaborerà la richiesta entro 30 giorni lavorativi</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Riceverai una conferma finale quando l'eliminazione sarà completata</span>
                </li>
              </ol>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email dell'account OBC Cloud</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="La tua email registrata"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="delete-account-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo della richiesta (opzionale)</Label>
                <Textarea
                  id="reason"
                  placeholder="Aiutaci a migliorare: perché vuoi eliminare il tuo account?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  data-testid="delete-account-reason"
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={loading || !email}
                data-testid="delete-account-submit"
              >
                {loading ? 'Invio in corso...' : 'Invia Richiesta di Eliminazione'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Data Deletion Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5" />
              Dati che verranno eliminati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Quando la tua richiesta sarà elaborata, i seguenti dati verranno <strong>eliminati permanentemente</strong>:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong>Dati del profilo:</strong> nome, email, password criptata, impostazioni account</span>
              </li>
              <li className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong>OBC:</strong> tutti i link e le configurazioni OBC create</span>
              </li>
              <li className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong>Ticket di supporto:</strong> tutte le conversazioni e richieste di assistenza</span>
              </li>
              <li className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong>Sessioni e dispositivi:</strong> token di accesso e dispositivi autorizzati</span>
              </li>
              <li className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong>Preferenze 2FA:</strong> impostazioni autenticazione a due fattori</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Retention Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5" />
              Dati conservati e periodi di conservazione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Per obblighi legali e fiscali, alcuni dati potrebbero essere conservati per un periodo limitato:
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Dati di fatturazione e transazioni:</strong>
                  <p className="text-muted-foreground">Conservati per 10 anni come richiesto dalla normativa fiscale italiana</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Log di sicurezza (anonimi):</strong>
                  <p className="text-muted-foreground">Conservati per 12 mesi per prevenzione frodi, poi eliminati automaticamente</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Backup di sistema:</strong>
                  <p className="text-muted-foreground">Eliminati entro 90 giorni dalla richiesta</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Warning */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-yellow-200 mb-1">Attenzione: azione irreversibile</p>
              <p className="text-yellow-200/80">
                L'eliminazione dell'account è permanente e non può essere annullata. 
                Tutti i tuoi dati, OBC, ticket e cronologia verranno eliminati definitivamente.
                Assicurati di aver esportato eventuali dati importanti prima di procedere.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground pt-4 border-t border-border">
          <p>OBC Cloud - Gestione Supervisione Domotica OBC</p>
          <p className="mt-1">
            Per assistenza: <a href="mailto:support@obcevo.com" className="text-primary hover:underline">support@obcevo.com</a>
          </p>
          <p className="mt-2">
            <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
